import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useRegisterSW } from 'virtual:pwa-register/react';

/** Segundos para o usuário salvar o que está fazendo antes do reload automático. */
const AUTO_UPDATE_SECONDS = 60;

/**
 * Prompt PWA no canto inferior direito:
 * - botão "Atualizar agora"
 * - countdown que aplica a atualização sozinho se o usuário não clicar
 */
export default function PwaUpdatePrompt() {
    const [open, setOpen] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(AUTO_UPDATE_SECONDS);
    const dismissedRef = useRef(false);
    const updatingRef = useRef(false);

    const {
        needRefresh: [needRefresh, setNeedRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        immediate: true,
        onRegisterError(error) {
            console.error('[PWA] falha ao registrar service worker', error);
        },
    });

    const applyUpdate = useCallback(() => {
        if (updatingRef.current) return;
        updatingRef.current = true;
        setOpen(false);
        void updateServiceWorker(true);
    }, [updateServiceWorker]);

    const postpone = useCallback(() => {
        dismissedRef.current = true;
        setOpen(false);
        setNeedRefresh(false);
    }, [setNeedRefresh]);

    useEffect(() => {
        if (!needRefresh || dismissedRef.current) return;
        setSecondsLeft(AUTO_UPDATE_SECONDS);
        setOpen(true);
    }, [needRefresh]);

    useEffect(() => {
        if (!open) return undefined;

        if (secondsLeft <= 0) {
            applyUpdate();
            return undefined;
        }

        const id = window.setTimeout(() => {
            setSecondsLeft(prev => prev - 1);
        }, 1000);

        return () => window.clearTimeout(id);
    }, [open, secondsLeft, applyUpdate]);

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                // Clique fora / Esc = adiar (não força update)
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    postpone();
                }
            }}
            hideBackdrop
            disableEnforceFocus
            disableScrollLock
            aria-labelledby="pwa-update-title"
            sx={{
                pointerEvents: 'none',
                '& .MuiDialog-container': {
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                    pointerEvents: 'none',
                },
                '& .MuiPaper-root': {
                    pointerEvents: 'auto',
                    m: 2,
                    mb: 3,
                    mr: 3,
                    maxWidth: 400,
                    width: '100%',
                    boxShadow: 8,
                },
            }}
        >
            <DialogTitle id="pwa-update-title" variant="h6">
                Nova versão disponível
            </DialogTitle>
            <DialogContent dividers>
                <DialogContentText component="div">
                    Há uma atualização do painel. Salve o que estiver fazendo e atualize
                    quando puder.
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        A página será atualizada automaticamente em{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="primary">
                            {secondsLeft}s
                        </Typography>
                        .
                    </Typography>
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button variant="text" onClick={postpone}>
                    Depois
                </Button>
                <Button variant="contained" color="primary" onClick={applyUpdate}>
                    Atualizar agora
                </Button>
            </DialogActions>
        </Dialog>
    );
}
